import { all, put, takeLatest } from 'redux-saga/effects';
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request';
import {
  CANCEL_EVENT_REGISTRATION,
  CREATE_EVENT_APPLICATION,
  CREATE_STRIPE_CHECKOUT_SESION,
  FIND_VENDOR_EVENT_REGISTERED,
  GET_ALL_VENDOR_EVENTS,
  GET_VENDOR_EVENTS,
  GET_VENDOR_EVENTS_BY_EVENT,
  GET_VENDOR_EVENTS_WITHOUT_FILTERS,
  REGISTER_VENDOR_EVENT,
  UPDATE_APPLICATION_STATUS,
  REFUND,
  GET_REFUNDS,
  CREATE_REFUND_REQUEST,
  GET_VENDOR_UNPAID_EVENT_REGISTRATIONS,
  GET_VENDOR_PAID_EVENT_REGISTRATIONS,
} from './constants';
import Api from '../../commons/api';
import * as VendorEventActions from './action';
import { APPLICATION_STATUS, PAYMENT_STATUS } from '../../constants/event-application-status';
import { formatDateStandard } from '../../utils';

const controller = 'vendor-events';
const createBaseQueryBuilder = () =>
  RequestQueryBuilder.create()
    .setJoin({ field: 'vendor_event_contact' })
    .setJoin({ field: 'vendor_event_features' });

function* registerVendorEvent({ payload }) {
  try {
    const { data } = yield Api.post(`${controller}`, payload);
    yield put(VendorEventActions.registerVendorEventSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.registerVendorEventFailed(err));
  }
}

function* fetchGetVendorEvents({ payload }) {
  try {
    const query = createBaseQueryBuilder()
      .setJoin({ field: 'event' })
      .setJoin({ field: 'event.parameters_event' })
      .setFilter({
        field: 'fk_vendor',
        operator: '$eq',
        value: payload.vendorId,
      })
      .setFilter({
        field: 'status',
        operator: CondOperator.IN,
        value: [PAYMENT_STATUS.BALANCE_DUE, PAYMENT_STATUS.PAID],
      })
      .setFilter({
        field: 'has_been_cancelled',
        operator: CondOperator.EQUALS,
        value: false,
      });

    const { data } = yield Api.get(`${controller}?${query.query()}`);
    yield put(VendorEventActions.getVendorEventSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.getVendorEventsFailed(err));
  }
}

function* fetchGetVendorEventsWithoutFilters({ payload }) {
  try {
    const query = createBaseQueryBuilder().setJoin({ field: 'event' }).setFilter({
      field: 'fk_vendor',
      operator: '$eq',
      value: payload.vendorId,
    });

    const { data } = yield Api.get(`${controller}?${query.query()}`);
    yield put(VendorEventActions.getVendorEventsWithoutFilterSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.getVendorEventsWithoutFiltersFailed(err));
  }
}

function* findVendorEventRegistered({ payload }) {
  try {
    const query = createBaseQueryBuilder()
      .setJoin({ field: 'vendor' })
      .setFilter({
        field: 'fk_vendor',
        operator: '$eq',
        value: payload.vendorId,
      })
      .setFilter({
        field: 'fk_event',
        operator: '$eq',
        value: payload.eventId,
      });
    const { data } = yield Api.get(`${controller}?${query.query()}`);
    if (!data.length) {
      yield put(VendorEventActions.vendorEventNotRegistered(data));
    } else {
      yield put(VendorEventActions.registerVendorEventSuccess(data[0]));
    }
  } catch (err) {
    yield put(VendorEventActions.getVendorEventsFailed(err));
  }
}

function* updateVendorEventApplicationStatus({ payload }) {
  try {
    const { vendorId, eventId, newStatus, onStatusChanged } = payload;
    const { data } = yield Api.put(`${controller}/${controller}/${vendorId}/${eventId}`, {
      newStatus,
    });
    onStatusChanged(data);
    yield put(VendorEventActions.updateEventApplicationStatusSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.updateEventApplicationStatusFailed(err));
  }
}

function* createStripeCheckoutSession({ payload }) {
  try {
    const url = `${controller}/vendor-events/create-checkout-session/`;
    const { data } = yield Api.post(url, payload);
    yield put(VendorEventActions.createStripeCheckoutSessionSuccess(data));
  } catch (error) {
    yield put(VendorEventActions.createStripeCheckoutSessionFailed(error));
  }
}

function* createEventApplication({ payload }) {
  try {
    const url = `${controller}/${controller}/apply/`;
    const { data } = yield Api.post(url, payload);
    yield put(VendorEventActions.createEventApplicationSuccess(data));
  } catch (error) {
    yield put(VendorEventActions.createEventApplicationFailed(error));
  }
}

function* fetchGetVendorEventsByEvent({ payload }) {
  try {
    const query = RequestQueryBuilder.create()
      .setJoin({ field: 'event' })
      .setJoin({ field: 'vendor' })
      .setJoin({ field: 'space_dimension', select: ['industry', 'cost', 'size'] })
      .setJoin({ field: 'vendor_event_features', select: ['id'] })
      .setJoin({ field: 'vendor_event_features.feature', select: ['name', 'price'] })
      .setFilter({
        field: 'fk_event',
        operator: CondOperator.EQUALS,
        value: payload.eventId,
      })
      .sortBy([
        { field: 'total_price', order: 'DESC' },
        { field: 'vendor_event_features.feature.price', order: 'DESC' },
      ]);

    let { data } = yield Api.get(`${controller}?${query.query()}`);
    data = data.map((item) =>
      item.application_status !== APPLICATION_STATUS.APPROVED ? { ...item, status: '' } : item,
    );

    yield put(VendorEventActions.getVendorEventsByEventSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.getVendorEventsByEventFailed(err));
  }
}

function* fetchGetAllVendorEvents() {
  try {
    const query = RequestQueryBuilder.create()
      .setJoin([
        { field: 'event' },
        { field: 'vendor' },
        { field: 'event.venue' },
        { field: 'event.parameters_event' },
      ])
      .sortBy({ field: 'event.parameters_event.event_date', order: 'DESC' });

    let { data } = yield Api.get(`${controller}?${query.query()}`);
    data = data.map((item) => {
      if (item) {
        /* eslint-disable no-param-reassign */
        if (item.application_status !== APPLICATION_STATUS.APPROVED) item.status = '';
        /* eslint-disable no-param-reassign */
        item.event.parameters_event.event_date = formatDateStandard(
          item.event.parameters_event.event_date,
        );
      }
      return item;
    });

    yield put(VendorEventActions.getAllVendorEventsSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.getAllVendorEventsFailed(err));
  }
}

function* cancelEventRegistration({ payload }) {
  try {
    const { data } = yield Api.post(`${controller}/${controller}/cancel-registration`, payload);
    yield put(VendorEventActions.cancelEventRegistrationSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.cancelEventRegistrationFailed(err));
  }
}

function* createRefund({ payload }) {
  try {
    let assetId = null;
    if (payload.asset) {
      const { data } = yield Api.post(`users/users/add-asset/`, payload.asset);
      assetId = data.id;
    }
    const newPayload = {
      amount: parseFloat(payload.amountRequested),
      description: payload.reason,
      status: 'pending',
      fk_assets: assetId,
      fk_event: payload.eventId,
      fk_user: payload.userId,
    };
    const { data2 } = yield Api.post(`${controller}/${controller}/refund`, newPayload);
    yield put(VendorEventActions.createRefundSuccess(data2));
  } catch (err) {
    console.log('error', err);
    yield put(VendorEventActions.createRefundFailed(err));
  }
}

function* getRefund({ payload }) {
  try {
    const { data } = yield Api.get(`${controller}/${controller}/refund/${payload.eventId}`);
    yield put(VendorEventActions.getRefundSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.getRefundFailed(err));
  }
}

function* createRefundRequest({ payload }) {
  try {
    const { data } = yield Api.post(`${controller}/${controller}/refund-request`, payload);
    yield put(VendorEventActions.createRefundRequestSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.createRefundRequestFailed(err));
  }
}

function* fetchVendorUnpaidEventRegistrations({ payload }) {
  try {
    const query = RequestQueryBuilder.create()
      .setJoin({ field: 'event' })
      .setJoin({ field: 'event.venue' })
      .setJoin({ field: 'event.parameters_event' })
      .setJoin({ field: 'space_dimension' })
      .setFilter({
        field: 'fk_vendor',
        operator: CondOperator.EQUALS,
        value: payload.vendorId,
      })
      .setFilter({
        field: 'status',
        operator: CondOperator.IN,
        value: [PAYMENT_STATUS.UNPAID, PAYMENT_STATUS.BALANCE_DUE],
      })
      .setFilter({
        field: 'has_been_cancelled',
        operator: CondOperator.EQUALS,
        value: false,
      })
      .sortBy({ field: 'event.parameters_event.event_date', order: 'ASC' });

    const { data } = yield Api.get(`${controller}?${query.query()}`);
    yield put(VendorEventActions.getVendorUnpaidEventRegistrationsSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.getVendorUnpaidEventRegistrationsFailed(err));
  }
}

function* fetchVendorPaidEventRegistrations({ payload }) {
  try {
    const query = RequestQueryBuilder.create()
      .setJoin({ field: 'event' })
      .setJoin({ field: 'event.venue' })
      .setJoin({ field: 'event.parameters_event' })
      .setJoin({ field: 'space_dimension' })
      .setFilter({
        field: 'fk_vendor',
        operator: CondOperator.EQUALS,
        value: payload.vendorId,
      })
      .setFilter({
        field: 'status',
        operator: CondOperator.EQUALS,
        value: PAYMENT_STATUS.PAID,
      })
      .setFilter({
        field: 'has_been_cancelled',
        operator: CondOperator.EQUALS,
        value: false,
      })
      .sortBy({ field: 'event.parameters_event.event_date', order: 'DESC' });

    const { data } = yield Api.get(`${controller}?${query.query()}`);
    yield put(VendorEventActions.getVendorPaidEventRegistrationsSuccess(data));
  } catch (err) {
    yield put(VendorEventActions.getVendorPaidEventRegistrationsFailed(err));
  }
}

function* ActionWatcher() {
  yield takeLatest(GET_VENDOR_EVENTS, fetchGetVendorEvents);
  yield takeLatest(GET_VENDOR_EVENTS_WITHOUT_FILTERS, fetchGetVendorEventsWithoutFilters);
  yield takeLatest(FIND_VENDOR_EVENT_REGISTERED, findVendorEventRegistered);
  yield takeLatest(REGISTER_VENDOR_EVENT, registerVendorEvent);
  yield takeLatest(UPDATE_APPLICATION_STATUS, updateVendorEventApplicationStatus);
  yield takeLatest(CREATE_STRIPE_CHECKOUT_SESION, createStripeCheckoutSession);
  yield takeLatest(CREATE_EVENT_APPLICATION, createEventApplication);
  yield takeLatest(GET_VENDOR_EVENTS_BY_EVENT, fetchGetVendorEventsByEvent);
  yield takeLatest(GET_ALL_VENDOR_EVENTS, fetchGetAllVendorEvents);
  yield takeLatest(CANCEL_EVENT_REGISTRATION, cancelEventRegistration);
  yield takeLatest(REFUND, createRefund);
  yield takeLatest(GET_REFUNDS, getRefund);
  yield takeLatest(CREATE_REFUND_REQUEST, createRefundRequest);
  yield takeLatest(GET_VENDOR_UNPAID_EVENT_REGISTRATIONS, fetchVendorUnpaidEventRegistrations);
  yield takeLatest(GET_VENDOR_PAID_EVENT_REGISTRATIONS, fetchVendorPaidEventRegistrations);
}

export default function* EventSaga() {
  yield all([ActionWatcher()]);
}
